import './index.less';

import { Col, Row, Typography, Input, Collapse, message, Result } from 'antd';
import { MobileDatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import TextField from '@mui/material/TextField';
import dayjs from 'dayjs';
import React, { useEffect } from 'react';

import { numberWithCommas, roundTo } from '../../helpers/number';
import { displayDate } from '../../utils/datetime.util';
import { RefinanceWithAccountDetails } from '../../features/refinance/refinanceSlice';
import {
  calcRefinanceAdjustedOutstandingBalance,
  onCalculateEstimatedMonthlyAmount,
  onCalculateFinalInstalmentAmount,
  onCalculateTotalInstalmentAmount,
  onCalculateTotalInterest,
} from '../../utils/calculation.util';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '../../store/store';
import {
  getRefinanceDetailsThunk,
  updateRefinanceEffectiveDateThunk,
} from '../../services/refinanceService/refinanceThunk';
import UpdateInterestRateModal from '../../pages/Application/UpdateInterestRateModal';
import UpdateRefinancingFeesModal from '../../pages/Application/UpdateRefinancingFeesModal';
import UpdateFinanceAmountModal from '../../pages/Application/ApplicationDetails/UpdateFinanceAmountModal';
import UpdateLoanTenureModal from '../../pages/Application/ApplicationDetails/UpdateLoanTenureModal';
import AccountEnquiryModal from '../../pages/AccountEnquiry/AccountEnquiryModal';
import LoadingParagraph from '../LoadingParagraphLong';

const { Title } = Typography;
const { Panel } = Collapse;

interface RefinancingDetailsPanelProps {
  refinanceId: string;
  readOnly?: boolean;
  onUpdateRefinance: () => any;
}

const RefinancingDetailsPanel: React.FC<RefinancingDetailsPanelProps> = ({
  refinanceId,
  readOnly = false,
  onUpdateRefinance,
}) => {
  const dispatch = useDispatch<AppDispatch>();

  const [refinance, setRefinance] = React.useState<RefinanceWithAccountDetails | null>(null);
  const [loading, setLoading] = React.useState<boolean>(false);

  const onChangeEffectiveDate = async (value: dayjs.Dayjs | null) => {
    if (dayjs(value).isBefore(dayjs()) || dayjs(value).isAfter(dayjs().add(14, 'day'))) {
      message.error('Effective date must be within 14 days from current date.');
      return;
    }
    await dispatch(
      updateRefinanceEffectiveDateThunk({
        id: refinance?.id,
        effectiveDate: value,
      })
    )
      .unwrap()
      .then((res) => {
        message.success('Refinance effective date updated successfully');
        onUpdateRefinance();
        return res;
      })
      .catch((error) => {
        return error;
      });
  };

  const totalRefinanceAmount =
    Number(refinance?.totalLoanOutstanding) + Number(refinance?.refinanceFee);

  const originalLoanData = [
    {
      title: 'Agreement No',
      value: (
        <AccountEnquiryModal agreementNo={refinance?.oldAgreement?.agreementNo ?? ''} isText />
      ),
    },
    {
      title: 'Agreement Date',
      value: displayDate(refinance?.oldAgreement?.signDate) || '-',
    },
    {
      title: 'Original Finance Amount (RM)',
      value: refinance?.oldAgreement
        ? numberWithCommas(refinance?.oldAgreement.application?.financeAmount)
        : '-',
    },
    {
      title: 'Original Interest Rate (%)',
      value: refinance?.oldAgreement?.application?.loanInterestRate
        ? `${refinance?.oldAgreement.application?.loanInterestRate}%`
        : '-',
    },
    {
      title: 'Original Loan Tenure (months)',
      value: refinance?.oldAgreement?.application?.loanTenure || '-',
    },
    {
      title: 'Original Interest Amount (RM)',
      value: refinance?.oldAgreement?.application?.financeAmount
        ? numberWithCommas(
            (((refinance?.oldAgreement.application?.financeAmount *
              refinance?.oldAgreement.application?.loanInterestRate) /
              100) *
              refinance?.oldAgreement.application?.loanTenure) /
              12
          )
        : '-',
    },
    {
      title: 'Total Original Instalment (RM)',
      value: refinance?.oldAgreement?.application?.financeAmount
        ? numberWithCommas(
            refinance?.oldAgreement.application?.financeAmount +
              (((refinance?.oldAgreement.application?.financeAmount *
                refinance?.oldAgreement.application?.loanInterestRate) /
                100) *
                refinance?.oldAgreement.application.loanTenure) /
                12
          )
        : '-',
    },
    {
      title: 'Initial Monthly Instalment (RM)',
      value: refinance?.oldAgreement?.monthlyInstalmentAmount
        ? numberWithCommas(refinance?.oldAgreement?.monthlyInstalmentAmount)
        : '-',
    },
    {
      title: 'Initial Final Instalment (RM)',
      value: refinance?.oldAgreement?.lastInstalmentAmount
        ? numberWithCommas(refinance?.oldAgreement?.lastInstalmentAmount)
        : '-',
    },
  ];

  const loanPerformanceData = [
    {
      value2: <strong>Amount (RM)</strong>,
    },
    {
      title: 'Month in Arrears (MIA)',
      value1: refinance?.oldAccountMia ?? refinance?.accountDetails?.mia ?? '-',
      value2: numberWithCommas(
        refinance?.oldAccountTotalDueInstalment ??
          refinance?.accountDetails?.totalDueInstalment ??
          0
      ),
    },
    {
      title: 'Paid Instalment Records',
      value1:
        refinance?.oldAccountPaidInstalmentMonth ?? refinance?.accountDetails?.paidInstalmentMonth,
      value2: numberWithCommas(
        refinance?.oldAccountTotalPaidInstalment ??
          refinance?.accountDetails.totalPaidInstalment ??
          0
      ),
    },
    {
      title: 'Last Payment Date & Amount',
      value1: refinance?.oldAccountLastPaymentDate
        ? displayDate(refinance?.oldAccountLastPaymentDate)
        : refinance?.accountDetails.lastPaymentDate
        ? displayDate(refinance?.accountDetails.lastPaymentDate)
        : '-',
      value2: numberWithCommas(
        refinance?.oldAccountLastPaymentAmount ?? refinance?.accountDetails.lastPaymentAmount ?? 0
      ),
    },
    {
      title: 'Remaining Upcoming Instalment',
      value1:
        (refinance?.oldAccountTotalInstalmentMonth ??
          refinance?.accountDetails.totalInstalmentMonth ??
          0) -
        Math.max(
          refinance?.oldAccountPaidInstalmentMonth ??
            refinance?.accountDetails.paidInstalmentMonth ??
            0,
          (refinance?.oldAccountCurrentTenureNo ?? refinance?.accountDetails.currentTenureNo ?? 0) -
            1
        ),
      value2: numberWithCommas(
        (refinance?.oldAccountInstalmentBalance ??
          refinance?.accountDetails.instalmentBalance ??
          0) -
          (refinance?.oldAccountTotalDueInstalment ??
            refinance?.accountDetails.totalDueInstalment ??
            0)
      ),
    },
    {
      title: 'Total Instalment Balance',
      value2: numberWithCommas(
        refinance?.oldAccountInstalmentBalance ?? refinance?.accountDetails.instalmentBalance ?? 0
      ),
    },
    {
      title: 'Total Charges',
      value2: numberWithCommas(
        refinance?.oldAccountTotalCharges ?? refinance?.accountDetails.totalCharges ?? 0
      ),
    },
  ];

  useEffect(() => {
    setLoading(true);
    dispatch(getRefinanceDetailsThunk({ refinanceId }))
      .unwrap()
      .then((res) => {
        setRefinance(res);
        return res;
      })
      .catch((err) => message.error(err))
      .finally(() => {
        setLoading(false);
      });
  }, [refinanceId]);

  return (
    <Collapse ghost expandIconPosition="end" defaultActiveKey={['refinancingDetails']}>
      <Panel
        key="refinancingDetails"
        className="application-details-section-container"
        header={<h3 className="application-details-section-title">Refinancing Details</h3>}
      >
        {loading ? (
          <LoadingParagraph />
        ) : refinance ? (
          <div className="refinancing-details-panel">
            <Row gutter={[24, 24]}>
              {/* Left Column */}
              <Col xs={24} lg={12}>
                {/* Original Loan Details */}
                <div className="details-section">
                  <Title level={5} className="section-title">
                    ORIGINAL LOAN DETAILS
                  </Title>
                  <Row gutter={[16, 16]}>
                    {originalLoanData.map((item, index) => (
                      <Col key={index} xs={24} sm={12} md={24} lg={24}>
                        <div className="info-field">
                          <div className="field-title">{item.title}</div>
                          <div className="field-value">{item.value}</div>
                        </div>
                      </Col>
                    ))}
                  </Row>
                </div>

                {/* Loan Performance */}
                <div className="details-section">
                  <Title level={5} className="section-title">
                    LOAN PERFORMANCE
                  </Title>
                  <Row gutter={[16, 16]}>
                    {loanPerformanceData.map((item, index) => (
                      <Col key={index} span={24}>
                        <div className="info-field">
                          <Col className="field-title" span={12}>
                            {item.title}
                          </Col>
                          <Col className="info-field" span={12}>
                            <Col className="field-value" span={12}>
                              {item.value1}
                            </Col>
                            <Col className="field-value" span={12}>
                              {item.value2}
                            </Col>
                          </Col>
                        </div>
                      </Col>
                    ))}
                  </Row>
                </div>
              </Col>

              {/* Right Column */}
              <Col xs={24} lg={12}>
                {/* Refinancing Loan Details */}
                <div className="details-section flex flex-col gap-4">
                  <Title level={5} className="section-title">
                    REFINANCING LOAN DETAILS
                  </Title>
                  <Row className="info-field">
                    {/* New Effective Date - Editable */}
                    <Col span={readOnly ? 24 : 20}>
                      <Row className="info-content">
                        <Col span={15} className="field-title">
                          <div>New Effective Date</div>
                        </Col>
                        <Col span={8} className="field-value">
                          {readOnly ? (
                            <div>{displayDate(refinance?.effectiveDate) || '-'}</div>
                          ) : (
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                              <MobileDatePicker
                                value={
                                  refinance.effectiveDate ? dayjs(refinance.effectiveDate) : null
                                }
                                minDate={dayjs().add(1, 'day')}
                                maxDate={dayjs().add(14, 'day')}
                                onChange={(date: dayjs.Dayjs | null) => onChangeEffectiveDate(date)}
                                inputFormat={'DD/MM/YYYY'}
                                showToolbar={false}
                                disablePast
                                className="editable-field w-full"
                                renderInput={(params) => {
                                  return (
                                    <TextField
                                      {...params}
                                      label=""
                                      variant="standard"
                                      placeholder="Select Date"
                                      InputProps={{ disableUnderline: true }}
                                      sx={{
                                        input: {
                                          textAlign: 'center',
                                          fontSize: 14,
                                          color: '#f02c29 !important',
                                          cursor: 'pointer',
                                          pointerEvents: 'auto !important',
                                          '::placeholder': {
                                            color: '#000000 !important',
                                          },
                                          borderRadius: '6px',
                                          borderColor: '#e40308 !important',
                                          border: '1px solid transparent',
                                          paddingRight: '8px',
                                          caretColor: 'transparent',
                                        },
                                      }}
                                    />
                                  );
                                }}
                              />
                            </LocalizationProvider>
                          )}
                        </Col>
                      </Row>
                    </Col>
                  </Row>
                  <Row className="info-field">
                    {/* Original Outstanding Balance (RM) - Read Only */}
                    <Col span={readOnly ? 24 : 20}>
                      <Row className="info-content">
                        <Col span={15} className="field-title">
                          <div>Original Outstanding Balance (RM)</div>
                        </Col>
                        <Col span={8}>
                          <Input
                            className="inputTextAlignRight"
                            disabled={true}
                            prefix=" "
                            value={numberWithCommas(refinance?.totalLoanOutstanding || 0) ?? '-'}
                          />
                        </Col>
                      </Row>
                    </Col>
                  </Row>
                  <Row className="info-field">
                    {/* Refinancing Fees - Editable */}
                    <Col span={readOnly ? 24 : 20}>
                      <Row className="info-content">
                        <Col span={15} className="field-title">
                          <div>Refinancing Fees</div>
                        </Col>
                        <Col span={8}>
                          <Input
                            className="inputTextAlignRight"
                            disabled={true}
                            prefix=" "
                            value={numberWithCommas(refinance.refinanceFee) ?? '-'}
                          />
                        </Col>
                      </Row>
                    </Col>
                    {!readOnly && (
                      <Col span={4} className="flex justify-end">
                        <UpdateRefinancingFeesModal
                          refinance={refinance}
                          callback={onUpdateRefinance}
                        />
                      </Col>
                    )}
                  </Row>
                  <Row className="info-field">
                    {/* Final Outstanding Balance - Read Only s*/}
                    <Col span={readOnly ? 24 : 20}>
                      <Row className="info-content">
                        <Col span={15} className="field-title">
                          <div>Final Outstanding Balance (RM)</div>
                        </Col>
                        <Col span={8}>
                          <Input
                            className="inputTextAlignRight"
                            disabled={true}
                            prefix=" "
                            value={numberWithCommas(totalRefinanceAmount || 0) ?? '-'}
                          />
                        </Col>
                      </Row>
                    </Col>
                  </Row>
                  <Row className="info-field">
                    {/* Refinancing Amount - Editable */}
                    <Col span={readOnly ? 24 : 20}>
                      <Row className="info-content">
                        <Col span={15} className="field-title">
                          <div>Refinancing Amount (RM)</div>
                        </Col>
                        <Col span={8}>
                          <Input
                            className="inputTextAlignRight"
                            disabled={true}
                            prefix=" "
                            value={numberWithCommas(refinance.application.financeAmount) ?? '-'}
                          />
                        </Col>
                      </Row>
                    </Col>
                    {!readOnly && (
                      <Col span={4} className="flex justify-end">
                        <UpdateFinanceAmountModal
                          applicationDetails={refinance.application}
                          callback={onUpdateRefinance}
                          title="Update Refinancing Amount"
                          customRules={[
                            {
                              required: true,
                              message: 'Refinancing amount is required',
                            },
                            {
                              type: 'number',
                              max: totalRefinanceAmount || 0,
                              message:
                                'Refinancing amount cannot be more than ' +
                                numberWithCommas(totalRefinanceAmount || 0),
                            },
                            {
                              type: 'number',
                              min: 1,
                              message: 'Refinancing amount must be at least RM 1.00',
                            },
                          ]}
                        />
                      </Col>
                    )}
                  </Row>
                  <Row className="info-field">
                    {/* New Interest Rate - Editable */}
                    <Col span={readOnly ? 24 : 20}>
                      <Row className="info-content">
                        <Col span={15} className="field-title">
                          <div>New Interest Rate (%)</div>
                        </Col>
                        <Col span={8}>
                          <Input
                            className="inputTextAlignRight"
                            disabled={true}
                            prefix=" "
                            value={refinance.application.loanInterestRate ?? '-'}
                          />
                        </Col>
                      </Row>
                    </Col>
                    {!readOnly && (
                      <Col span={4} className="flex justify-end">
                        <UpdateInterestRateModal
                          applicationDetails={refinance.application}
                          callback={onUpdateRefinance}
                        />
                      </Col>
                    )}
                  </Row>
                  <Row className="info-field">
                    {/* Refinance Loan Tenure - Editable */}
                    <Col span={readOnly ? 24 : 20}>
                      <Row className="info-content">
                        <Col span={15} className="field-title">
                          <div>Refinance Loan Tenure (months)</div>
                        </Col>
                        <Col span={8}>
                          <Input
                            className="inputTextAlignRight"
                            disabled={true}
                            prefix=" "
                            value={refinance.application.loanTenure ?? '-'}
                          />
                        </Col>
                      </Row>
                    </Col>
                    {!readOnly && (
                      <Col span={4} className="flex justify-end">
                        <UpdateLoanTenureModal
                          applicationDetails={refinance.application}
                          callback={onUpdateRefinance}
                        />
                      </Col>
                    )}
                  </Row>
                  <Row className="info-field">
                    {/* Refinance Interest Amount (RM) - Read Only */}
                    <Col span={readOnly ? 24 : 20}>
                      <Row className="info-content">
                        <Col span={15} className="field-title">
                          <div>Refinance Interest Amount (RM)</div>
                        </Col>
                        <Col span={8}>
                          <Input
                            className="inputTextAlignRight"
                            disabled={true}
                            prefix=" "
                            value={
                              numberWithCommas(
                                roundTo(
                                  onCalculateTotalInterest(
                                    refinance.application.financeAmount ?? 0,
                                    refinance.application.loanInterestRate,
                                    refinance.application.loanTenure
                                  ),
                                  2
                                )
                              ) ?? '-'
                            }
                          />
                        </Col>
                      </Row>
                    </Col>
                  </Row>
                  <Row className="info-field">
                    {/* Total Refinancing Instalment Amount (RM) - Read Only */}
                    <Col span={readOnly ? 24 : 20}>
                      <Row className="info-content">
                        <Col span={15} className="field-title">
                          <div>Total Refinancing Instalment Amount (RM)</div>
                        </Col>
                        <Col span={8}>
                          <Input
                            className="inputTextAlignRight"
                            disabled={true}
                            prefix=" "
                            value={
                              numberWithCommas(
                                roundTo(
                                  onCalculateTotalInstalmentAmount(
                                    refinance.application.financeAmount ?? 0,
                                    refinance.application.loanInterestRate,
                                    refinance.application.loanTenure
                                  ),
                                  2
                                )
                              ) ?? '-'
                            }
                          />
                        </Col>
                      </Row>
                    </Col>
                  </Row>
                  <Row className="info-field">
                    {/* New Monthly Instalment (RM) - Read Only */}
                    <Col span={readOnly ? 24 : 20}>
                      <Row className="info-content">
                        <Col span={15} className="field-title">
                          <div>New Monthly Instalment (RM)</div>
                        </Col>
                        <Col span={8}>
                          <Input
                            className="inputTextAlignRight"
                            disabled={true}
                            prefix=" "
                            value={
                              numberWithCommas(
                                roundTo(
                                  onCalculateEstimatedMonthlyAmount(
                                    refinance.application.financeAmount ?? 0,
                                    refinance.application.loanInterestRate,
                                    refinance.application.loanTenure
                                  ),
                                  2
                                )
                              ) ?? '-'
                            }
                          />
                        </Col>
                      </Row>
                    </Col>
                  </Row>
                  <Row className="info-field">
                    {/* New Final Instalment (RM) - Read Only */}
                    <Col span={readOnly ? 24 : 20}>
                      <Row className="info-content">
                        <Col span={15} className="field-title">
                          <div>New Final Instalment (RM)</div>
                        </Col>
                        <Col span={8}>
                          <Input
                            className="inputTextAlignRight"
                            disabled={true}
                            prefix=" "
                            value={
                              numberWithCommas(
                                roundTo(
                                  onCalculateFinalInstalmentAmount(
                                    refinance.application.financeAmount ?? 0,
                                    refinance.application.loanInterestRate,
                                    refinance.application.loanTenure
                                  ),
                                  2
                                )
                              ) ?? '-'
                            }
                          />
                        </Col>
                      </Row>
                    </Col>
                  </Row>
                </div>

                {/* Pre-Execution Payment */}
                <div className="details-section">
                  <Title level={5} className="section-title">
                    PRE-EXECUTION PAYMENT
                  </Title>
                  <Row gutter={[16, 16]}>
                    <Col span={24}>
                      <div className="info-field">
                        <div className="field-title">Adjusted Outstanding Balance</div>
                        <div className="field-value">
                          RM{' '}
                          {numberWithCommas(
                            calcRefinanceAdjustedOutstandingBalance(
                              Number(refinance.totalLoanOutstanding),
                              Number(refinance.refinanceFee),
                              Number(refinance.application.financeAmount)
                            )
                          )}
                        </div>
                      </div>
                    </Col>
                  </Row>
                </div>
              </Col>
            </Row>
          </div>
        ) : (
          <Result
            status="500"
            title="Unable to load refinancing details"
            subTitle="Something went wrong while fetching the data. Please try again later."
          />
        )}
      </Panel>
    </Collapse>
  );
};

export default RefinancingDetailsPanel;
